import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions } from '@react-navigation/native';
import { Stack, useLocalSearchParams, useNavigation, usePathname, useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import LeafletingInfo from '@/components/LeafletingInfo';
import CrossPlatformMapView from '@/components/MapView';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundLeafletingGeometry, useBackgroundLeafletingRoutes, useBackgroundMyActiveLeafletingRoutes, useBackgroundRouteSummary } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useLeafletingStore } from '@/stores/leafletingStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { authenticatedFetch } from '@/utils/auth';
import { calculateMapRegionFromFeatureCollection, extractPolygonsFromFeatureCollection } from '@/utils/geojson';

export default function AssignmentDetailsScreen() {
  const { assignmentId, roundId } = useLocalSearchParams<{ assignmentId: string; roundId: string }>();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const router = useRouter();
  const pathname = usePathname();
  
  // State management
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'map'>('info');
  
  // Theme-aware colors
  const backgroundColor = useThemeColor({}, 'background');
  const iconColor = useThemeColor({}, 'icon');
  const tintColor = useThemeColor({}, 'tint');
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const borderColor = useThemeColor({}, 'border' as any);
  
  // Get navigation context
  const { getSelectedBranch } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  
  // Fetch all routes for this round to get basic route information
  const { data: allRoutes, loading: routesLoading, error: routesError } = useBackgroundLeafletingRoutes(roundId || '');
  
  // Find the specific route assignment
  const routeAssignment = allRoutes?.find((route: any) => route.assignment_id === assignmentId);
  
  // Fetch my active routes to check if user is already assigned
  const { data: myActiveRoutes } = useBackgroundMyActiveLeafletingRoutes();
  
  // Check if user is already assigned to this route
  const isUserInAssignment = myActiveRoutes?.some((route: any) => route.assignment_id === assignmentId) || false;
  
  // Only fetch route summary (for streets list) if user is assigned
  const { data: routeSummary, loading: routeSummaryLoading, error: routeSummaryError } = useBackgroundRouteSummary(
    isUserInAssignment && assignmentId ? assignmentId : ''
  );

  // Fetch geometry data for map view
  const { data: geometryData } = useBackgroundLeafletingGeometry(
    roundId || '',
    assignmentId ? [assignmentId] : []
  );

  // Join route assignment
  const handleJoinAssignment = async () => {
    try {
      setJoining(true);
      setError(null);
      
      const response = await authenticatedFetch(
        selectedBranch?.branch_short_name,
        '/route-assignments/join-route-assignment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            route_assignment_id: assignmentId
          }),
        },
        'groundforce'
      );
      
      if (!response.ok) {
        throw new Error(`Failed to join route assignment: ${response.status}`);
      }
      
      // Refresh my active routes
      const store = useLeafletingStore.getState();
      await store.fetchMyActiveRoutes();
      
    } catch (err) {
      console.error('Error joining route assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to join route assignment');
    } finally {
      setJoining(false);
    }
  };

  // Leave route assignment
  const handleLeaveAssignment = async () => {
    try {
      setLeaving(true);
      setError(null);
      
      const response = await authenticatedFetch(
        selectedBranch?.branch_short_name,
        '/route-assignments/leave-route-assignment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            route_assignment_id: assignmentId
          }),
        },
        'groundforce'
      );
      
      if (!response.ok) {
        throw new Error(`Failed to leave route assignment: ${response.status}`);
      }
      
      // Clear all data for this assignment from the store
      const store = useLeafletingStore.getState();
      store.clearAssignmentData(assignmentId as string);
      
      // Refresh my active routes
      await store.fetchMyActiveRoutes();
      
      // Navigate back to round details
      router.back();
      
    } catch (err) {
      console.error('Error leaving route assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to leave route assignment');
    } finally {
      setLeaving(false);
    }
  };

  // Handle status update (for completion)
  const handleStatusUpdate = async () => {
    try {
      // Refresh all relevant data after status update
      const store = useLeafletingStore.getState();
      await Promise.all([
        store.fetchMyActiveRoutes(),
        store.fetchRoutes(roundId || '')
      ]);
    } catch (err) {
      console.error('Error refreshing data after status update:', err);
    }
  };

  // Map helper functions
  const initialRegion = useMemo(() => {
    if (!geometryData || !assignmentId) return null;
    const assignmentDetails = geometryData[assignmentId];
    return calculateMapRegionFromFeatureCollection(assignmentDetails?.geometry, 0.1);
  }, [geometryData, assignmentId]);

  // Convert geometry to MapPolygon[]
  const polygons = useMemo(() => {
    if (!geometryData || !assignmentId) return [];
    
    const assignmentDetails = geometryData[assignmentId];
    if (!assignmentDetails || !assignmentDetails.geometry || !assignmentDetails.geometry.features) return [];
    
    // If user has not joined the assignment, show standard fill color
    // If user has joined, show transparent fill (current behavior)
    const fillColor = isUserInAssignment ? 'rgba(0,122,255,0)' : 'rgba(0,122,255,0.2)';
    
    return extractPolygonsFromFeatureCollection(
      { type: 'FeatureCollection', features: assignmentDetails.geometry.features },
      {
        idPrefix: 'polygon',
        strokeColor: '#007AFF',
        strokeWidth: 2,
        fillColor: fillColor,
        propertyExtractor: (feature, index) => feature.id || `assignment-${assignmentId}-${index}`,
      }
    );
  }, [geometryData, assignmentId, isUserInAssignment]);

  return (
    <ThemedView style={[AppStyles.container, { backgroundColor }]}>
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>
                {routeAssignment ? `${routeAssignment.number_within_branch}: ${routeAssignment.route_name}` : 'Route Assignment'}
              </ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color="#007AFF" />
            </TouchableOpacity>
          ),
          headerRight: () => {
            const { getSelectedBranch } = useNavigationStore.getState();
            const selectedBranch = getSelectedBranch();
            const hasGrantPermission = ['commander', 'lieutenant', 'sgt major'].includes((selectedBranch?.branch_role || '').toLowerCase());
            
            if (!hasGrantPermission) return null;
            
            return (
              <TouchableOpacity 
                onPress={() => router.push({ 
                  pathname: '/(main)/[branch]/share', 
                  params: { 
                    branch: selectedBranch?.branch_short_name || "", 
                    ref: pathname,
                    roundId: roundId,
                    assignmentId: assignmentId
                  } 
                })}
                style={{ 
                  padding: 8,
                  marginRight: 12,
                }}
              >
                <IconSymbol name="square.and.arrow.up" size={24} color="#007AFF" />
              </TouchableOpacity>
            );
          },
          headerBackVisible: false,
        }}
      />
      
      {/* Content Area */}
      <View style={{ flex: 1, paddingBottom: 60 + insets.bottom }}>
        {routesLoading ? (
          <View style={AppStyles.centeredContainer}>
            <ActivityIndicator size="large" color={iconColor} />
            <ThemedText style={{ marginTop: 16 }}>Loading assignment details...</ThemedText>
          </View>
        ) : routesError ? (
          <View style={AppStyles.centeredContainer}>
            <ThemedText style={AppStyles.errorText}>
              Error loading assignment details: {routesError.message}
            </ThemedText>
          </View>
        ) : !routeAssignment ? (
          <View style={AppStyles.centeredContainer}>
            <ThemedText style={AppStyles.errorText}>
              Assignment not found
            </ThemedText>
          </View>
        ) : error ? (
          <View style={AppStyles.centeredContainer}>
            <ThemedText style={AppStyles.errorText}>
              {error}
            </ThemedText>
          </View>
        ) : activeTab === 'info' ? (
          <LeafletingInfo 
            routeAssignment={routeAssignment}
            routeSummary={routeSummary}
            loading={isUserInAssignment && routeSummaryLoading}
            error={routeSummaryError?.message || null}
            insets={insets}
            isUserInAssignment={isUserInAssignment}
            onJoinAssignment={handleJoinAssignment}
            onLeaveAssignment={handleLeaveAssignment}
            joining={joining}
            leaving={leaving}
            onStatusUpdate={handleStatusUpdate}
          />
        ) : activeTab === 'map' ? (
          /* Map View */
          <View style={{ flex: 1 }}>
            {geometryData && assignmentId && geometryData[assignmentId] && polygons.length > 0 && initialRegion ? (
              <CrossPlatformMapView
                initialRegion={initialRegion}
                polygons={polygons}
                showUserLocation={true}
                style={{ flex: 1, width: '100%' }}
              />
            ) : (
              <View style={AppStyles.emptyContainer}>
                <ThemedText>Map data not available</ThemedText>
              </View>
            )}
          </View>
        ) : null}
      </View>

      {/* Bottom Tab Navigation */}
      <View style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        backgroundColor: backgroundColor,
        borderTopWidth: 1,
        borderTopColor: borderColor,
        paddingBottom: insets.bottom,
      }}>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            activeTab === 'info' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setActiveTab('info')}
        >
          <MaterialIcons 
            name="info" 
            size={24} 
            color={activeTab === 'info' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            activeTab === 'info' ? { color: tintColor } : { color: iconColor }
          ]}>Info</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            {
              flex: 1,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
            },
            activeTab === 'map' && { backgroundColor: cardBackground }
          ]}
          onPress={() => setActiveTab('map')}
        >
          <MaterialIcons 
            name="map" 
            size={24} 
            color={activeTab === 'map' ? tintColor : iconColor} 
          />
          <ThemedText style={[
            { fontSize: 12, marginTop: 4 },
            activeTab === 'map' ? { color: tintColor } : { color: iconColor }
          ]}>Map</ThemedText>
        </TouchableOpacity>
      </View>
    </ThemedView>
  );
}
